import { SupportedEnvironments } from '@/config/environments';
import { NaluriVideoError } from '@/errors';
import { ApiClient } from '@/utils/apiClient';
import type { TokenStore } from '@/utils/tokenStore';
import version from '@/utils/version';
import { NaluriVideoErrorConstants } from '..';
import { AccountApi } from './account.api';

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

const API_URL = {
  [SupportedEnvironments.DEV]: 'https://video-api.dev.naluri.net',
  [SupportedEnvironments.SIT]: 'https://video-api.staging.naluri.net',
  [SupportedEnvironments.PROD]: 'https://video-api.naluri.net',
} as const;

export class VideoApi {
  private readonly apiUrl: string;
  private readonly apiKey: string;

  private tokenStore: TokenStore;
  private accountApi: AccountApi;
  private onRefreshTokenError?: () => Promise<void>;

  constructor(options: {
    apiKey: string;
    environment: SupportedEnvironments;
    tokenStore: TokenStore;
    accountApi: AccountApi;
    onRefreshTokenError?: () => Promise<void>;
  }) {
    this.apiUrl = API_URL[options.environment];
    this.apiKey = options.apiKey;

    this.tokenStore = options.tokenStore;
    this.accountApi = options.accountApi;
    this.onRefreshTokenError = options.onRefreshTokenError;
  }

  /**
   * @returns {string} token to connect to a room
   * */
  public async authorizeUserForRoom(roomName: string): Promise<string> {
    const url = `${this.apiUrl}/rooms/${roomName}`;
    const options = {
      method: 'GET',
      headers: {
        'x-api-key': this.apiKey,
        'x-nl-sdk-version': version,
        'User-Agent': `Naluri Video SDK /JS (${version}})`,
      },
    };

    const apiClient = new ApiClient(
      this.tokenStore,
      this.accountApi,
      this.onRefreshTokenError,
    );

    const response = await apiClient.fetch(url, options);

    if (!response.ok) {
      const error = await response.json();
      if ('statusCode' in error && 'message' in error) {
        throw new NaluriVideoError({
          message: error.message,
          code: error.statusCode,
          statusCode: response.status,
        });
      }

      throw new NaluriVideoError({
        message: `Authorization failed for room ${roomName} with error: ${error.message}`,
        statusCode: response.status,
        code: NaluriVideoErrorConstants.UNKNOWN_ERROR,
      });
    }

    return await response.text();
  }
}
